import { useState, useEffect, useLayoutEffect, useRef, useCallback } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { AccessLockedModal } from './AccessLocked';
import NotificationCenter from './NotificationCenter';
import { useTutorial } from './tutorial/TutorialContext';
import { DOMAINS, hasDomainAccess, firstAccessiblePath, formatRole } from '../permissionConfig';
import '../sidebarTokens.css';
import '../sidebarMotion.css';

/**
 * SidebarNext — redesigned portal sidebar.
 *
 * Drop-in for layouts/Sidebar.jsx: same props plus optional `onLogout`, so
 * promoting it is a one-line import swap in each of the six domain layouts.
 *
 * ── Interaction model ────────────────────────────────────────────────────────
 * Rests at 64px showing icons only. Hovering (or focusing into it) peeks it open
 * to 260px as an OVERLAY — the 64px shell stays in flow, because otherwise every
 * accidental mouse-over would shove page content 196px sideways. The pin button
 * keeps it open permanently, persisted across sessions.
 *
 * Clicking outside closes a peek but deliberately does NOT un-pin: a pin that
 * any stray click destroys isn't a pin. On touch devices, where there is no
 * hover, tapping the rail opens a sticky peek and tapping outside closes it —
 * that's where click-outside actually earns its place.
 *
 * ── Icon positions ──────────────────────────────────────────────────────────
 * Every icon sits at exactly the same x whether collapsed or expanded. The
 * collapsed width is 64px = 2 x 32px, where 32px is the icon's centre when
 * expanded (12px nav padding + 12px row padding + 8px half-icon). Collapsing
 * only hides labels; nothing moves horizontally. See sidebarTokens.css.
 *
 * ── Visual language ─────────────────────────────────────────────────────────
 * The sliding indicator, shine sweep, fading dividers and avatar ring are
 * reimplemented from kokonutui components (Tailwind + shadcn + Framer Motion
 * upstream, plain CSS here). Values were taken from their shadcn registry and
 * are credited per rule in sidebarMotion.css. Their Smooth Tab spring
 * (stiffness 400, damping 30) works out to zeta 0.75 / 2.8% overshoot /
 * ~267ms, which `--sbn-spring` reproduces as a cubic-bezier.
 *
 * ── Fixed from pass 1 ───────────────────────────────────────────────────────
 *  - Contrast: locked items were 2.11:1 on white and 2.42:1 in dark, failing
 *    even the 3:1 non-text floor; icons were 3.01:1. Locked-ness is now carried
 *    by the lock icon, cursor and aria-disabled rather than by washing text out.
 *  - Focus: there were no focus styles at all. Now :focus-visible rings plus a
 *    skip link, so keyboard users aren't dragged through every nav item.
 *  - Home: pass 1 demoted "Back to Home" into a popover item. It's a permanent
 *    row again — losing the plain "get me out of here" control was a regression.
 *  - Sign out is NOT in the user menu. It stays on /landing via ProfileMenu.
 *  - The `user` prop is used. All six layouts pass it, but after the
 *    notification toggle moved to /landing nothing consumed it, so inside a
 *    domain there was no identity or role anywhere.
 */
export default function SidebarNext({
    navItems = [],
    sectionLabel = '',
    homeHref = '/landing',
    homeLabel = 'All Workspaces',
    theme,
    onThemeChange,
    themeOptions = THEME_OPTIONS,
    user,
    contentId = 'sbn-main',
    pinned: pinnedProp,
    domainKey,
}) {
    const navigate = useNavigate();
    const location = useLocation();
    const { startTour } = useTutorial();

    // Uncontrolled by default so the six layouts need no extra plumbing;
    // `pinned` can be passed to control it, which the preview page uses to show
    // several states at once without them sharing one stored flag.
    const controlled = pinnedProp !== undefined;
    const [pinnedState, setPinnedState] = useState(() => {
        try { return localStorage.getItem(PIN_KEY) === '1'; } catch { return false; }
    });
    const pinned = controlled ? pinnedProp : pinnedState;

    const [stickyPeek, setStickyPeek] = useState(false);   // touch / tap-to-open
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [menu, setMenu] = useState(null);                // 'workspace' | 'user' | null
    const [lockedLabel, setLockedLabel] = useState(null);
    const [entered, setEntered] = useState(false);         // drives the stagger

    const shellRef = useRef(null);
    const navRef = useRef(null);
    const indicatorRef = useRef(null);
    const drawerTriggerRef = useRef(null);

    const collapsed = !pinned && !stickyPeek;

    const togglePin = () => {
        const next = !pinned;
        setStickyPeek(false);
        if (controlled) return;
        setPinnedState(next);
        try { localStorage.setItem(PIN_KEY, next ? '1' : '0'); } catch { /* non-fatal */ }
    };

    // Stagger the nav in on mount. rAF rather than a timeout so it lands on the
    // first paint after layout, which is what makes the delays read evenly.
    useEffect(() => {
        const id = requestAnimationFrame(() => setEntered(true));
        return () => cancelAnimationFrame(id);
    }, []);

    // ── Sliding indicator ───────────────────────────────────────────────────
    // Measures the active row and moves ONE element to it (kokonutui Smooth Tab)
    // instead of every row painting its own fill. useLayoutEffect so the first
    // position is set before paint and the indicator doesn't visibly fly in from
    // 0 on mount.
    // Only the VERTICAL axis is measured. left/right come from CSS
    // (var(--sbn-nav-pad)), so the pill's width follows the rail automatically.
    // Measuring width in JS broke on hover, because a peek is pure CSS :hover
    // and never re-runs this effect — the pill kept the ~40px it measured while
    // the rail was still 64px.
    const measure = useCallback(() => {
        const nav = navRef.current;
        const bar = indicatorRef.current;
        if (!nav || !bar) return;
        const active = nav.querySelector('[aria-current="page"]');
        if (!active) { bar.dataset.ready = 'false'; return; }
        bar.style.transform = `translateY(${active.offsetTop}px)`;
        bar.style.height = `${active.offsetHeight}px`;
        bar.dataset.ready = 'true';
    }, []);

    useLayoutEffect(measure, [measure, location.pathname, navItems, collapsed]);

    // offsetTop still shifts when the rail opens — labels going from
    // position:absolute to static changes the caption's height — and hover does
    // that without any React state change. A ResizeObserver catches every cause
    // (hover, pin, drawer, font load, window resize) instead of trying to
    // enumerate them.
    useEffect(() => {
        const nav = navRef.current;
        if (!nav || typeof ResizeObserver === 'undefined') return;
        const ro = new ResizeObserver(() => measure());
        ro.observe(nav);
        return () => ro.disconnect();
    }, [measure]);

    // Close the drawer and any menu on navigation, or the drawer sits over the
    // page the user just asked for.
    useEffect(() => { setDrawerOpen(false); setMenu(null); setStickyPeek(false); }, [location.pathname]);

    // Click-outside: closes a menu, a sticky peek, and the drawer — but never
    // un-pins. mousedown rather than click so it fires before focus moves.
    useEffect(() => {
        const onDown = (e) => {
            if (shellRef.current?.contains(e.target)) return;
            setMenu(null);
            setStickyPeek(false);
        };
        document.addEventListener('mousedown', onDown);
        document.addEventListener('touchstart', onDown, { passive: true });
        return () => {
            document.removeEventListener('mousedown', onDown);
            document.removeEventListener('touchstart', onDown);
        };
    }, []);

    // Escape unwinds innermost-first and returns focus to the opener.
    useEffect(() => {
        if (!menu && !drawerOpen && !stickyPeek) return;
        const onKey = (e) => {
            if (e.key !== 'Escape') return;
            if (menu) { setMenu(null); return; }
            if (stickyPeek) { setStickyPeek(false); return; }
            if (drawerOpen) { setDrawerOpen(false); drawerTriggerRef.current?.focus(); }
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [menu, drawerOpen, stickyPeek]);

    const closeMenu = useCallback(() => setMenu(null), []);

    const displayName = user?.display_name || user?.username || '';
    const initials = (displayName || '?')
        .split(/\s+/).filter(Boolean).slice(0, 2).map(w => w[0]).join('').toUpperCase() || '?';

    // Only domains this user can reach — the switcher must not offer a
    // destination that immediately bounces them back to /landing.
    const switchable = DOMAINS.filter(d => hasDomainAccess(user, d.key));
    const isHome = location.pathname === homeHref;

    return (
        <>
            <div className="sbn-mobilebar">
                <button
                    ref={drawerTriggerRef}
                    type="button"
                    className="sbn-iconbtn"
                    aria-label="Open navigation"
                    aria-expanded={drawerOpen}
                    onClick={() => setDrawerOpen(true)}
                >
                    <IconMenu />
                </button>
                <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--sbn-text-strong)' }}>
                    {sectionLabel || 'Soulflower'}
                </span>
            </div>

            {drawerOpen && (
                <button
                    type="button"
                    className="sbn-scrim"
                    aria-label="Close navigation"
                    onClick={() => { setDrawerOpen(false); drawerTriggerRef.current?.focus(); }}
                />
            )}

            {/* The shell holds 64px in flow while the rail inside overlays. */}
            <div
                ref={shellRef}
                className="sbn-shell"
                data-pinned={pinned ? 'true' : 'false'}
                data-peek={stickyPeek ? 'true' : 'false'}
                // Touch has no hover, so a tap on the rail opens a sticky peek.
                onClick={() => { if (!pinned && !stickyPeek) setStickyPeek(true); }}
            >
                <aside
                    className="sbn"
                    data-collapsed={collapsed ? 'true' : 'false'}
                    data-open={drawerOpen ? 'true' : 'false'}
                >
                    <a className="sbn-skip" href={`#${contentId}`}>Skip to content</a>

                    {/* ── Brand + pin ─────────────────────────────────────────
                        The logo PNG is opaque white with black lettering (all four
                        corners measure rgba(255,255,255,255)), so painted onto a
                        dark rail it reads as an accidental white block. The padded
                        white chip makes that surface deliberate. Collapsed swaps
                        to a square monogram, because the 1253x241 wordmark spilled
                        straight out of the rail. */}
                    <div style={{ ...S.pad, display: 'flex', alignItems: 'center', gap: 8, minHeight: 52, overflow: 'hidden' }}>
                        {/* BOTH variants are always rendered and CSS picks one.
                            They used to be a React `collapsed ? ... : ...`, but
                            `collapsed` is state and the peek is pure CSS :hover —
                            so hovering widened the rail while the logo stayed a
                            monogram and the labels stayed hidden. Anything that
                            must change when the rail opens has to be driven by
                            the same conditions as the width. */}
                        <span className="sbn-logo-mark" style={S.logoMark} role="img" aria-label="Soulflower">S</span>
                        <span className="sbn-logo-full" style={S.logoChip}>
                            <img
                                src="/soulflower-logo.png"
                                alt="Soulflower"
                                style={{ height: 24, width: 'auto', display: 'block' }}
                                onError={e => { e.target.closest('span').textContent = 'Soulflower'; }}
                            />
                        </span>
                        <span className="sbn-logo-full" style={{ flex: 1, minWidth: 0 }} />
                        <button
                            type="button"
                            className="sbn-iconbtn sbn-pin sbn-logo-full"
                            onClick={(e) => { e.stopPropagation(); togglePin(); }}
                            aria-pressed={pinned}
                            aria-label={pinned ? 'Unpin sidebar' : 'Keep sidebar open'}
                            title={pinned ? 'Unpin sidebar' : 'Keep sidebar open'}
                        >
                            <IconPin filled={pinned} />
                        </button>
                    </div>

                    <hr className="sbn-rule" />

                    {/* ── Nav: home + workspace switcher + items ──────────────
                        One list so the sliding indicator can travel across all of
                        them, and so every icon shares the same 24px x offset. */}
                    <nav
                        ref={navRef}
                        className="sbn-nav"
                        data-entered={entered ? 'true' : 'false'}
                        aria-label={sectionLabel ? `${sectionLabel} navigation` : 'Navigation'}
                        style={{ position: 'relative', flex: 1, overflowY: 'auto', overflowX: 'hidden' }}
                    >
                        <div ref={indicatorRef} className="sbn-indicator" aria-hidden="true" />

                        {/* Home — a permanent row again, not buried in a popover. */}
                        <NavLink to={homeHref} end className="sbn-row" aria-current={isHome ? 'page' : undefined}>
                            <span className="sbn-icon"><IconHome /></span>
                            <span className="sbn-label sbn-collapsible">{homeLabel}</span>
                            <span className="sbn-tip">{homeLabel}</span>
                        </NavLink>

                        {sectionLabel && (
                            <button
                                type="button"
                                className="sbn-row"
                                aria-haspopup="menu"
                                aria-expanded={menu === 'workspace'}
                                onClick={(e) => { e.stopPropagation(); setMenu(m => (m === 'workspace' ? null : 'workspace')); }}
                                style={{ fontWeight: 600, color: 'var(--sbn-text-strong)' }}
                            >
                                <span className="sbn-icon" style={{ color: 'var(--sbn-icon-active)' }}><IconGrid /></span>
                                <span className="sbn-label sbn-collapsible">{sectionLabel}</span>
                                <span className="sbn-collapsible" style={{ display: 'flex', color: 'var(--sbn-icon)' }}>
                                    <IconChevron />
                                </span>
                                <span className="sbn-tip">{sectionLabel} — switch workspace</span>
                            </button>
                        )}

                        {menu === 'workspace' && (
                            <div className="sbn-pop" role="menu" style={{ left: 8, right: 8, top: 96 }}>
                                <div className="sbn-caption" style={{ padding: '4px 8px 6px' }}>Switch workspace</div>
                                {switchable.map(d => {
                                    const current = d.label === sectionLabel;
                                    return (
                                        <button
                                            key={d.key}
                                            type="button"
                                            role="menuitem"
                                            className="sbn-row"
                                            aria-current={current ? 'true' : undefined}
                                            onClick={() => {
                                                closeMenu();
                                                if (!current) navigate(firstAccessiblePath(user, d.key) || homeHref);
                                            }}
                                            style={current ? { color: 'var(--sbn-text-strong)', fontWeight: 600 } : undefined}
                                        >
                                            <span style={{ ...S.dot, background: d.color }} />
                                            <span className="sbn-label">{d.label}</span>
                                            {/* Marking the current one beats offering to navigate
                                                somewhere the user already is. */}
                                            {current && <span style={S.currentTag}>current</span>}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        <div className="sbn-caption" style={{ marginTop: 10 }}>
                            <span className="sbn-collapsible">Workspace</span>
                        </div>

                        {/* Always visible (not just after 5 auto-prompted sessions) —
                            lets anyone revisit this workspace's tour anytime, on top
                            of the auto-popped grand tour on first logins (App.js). */}
                        {domainKey && (
                            <button
                                type="button"
                                className="sbn-row"
                                onClick={() => startTour(domainKey)}
                                style={{ color: 'var(--sbn-text-strong)' }}
                            >
                                <span className="sbn-icon" aria-hidden="true">🎓</span>
                                <span className="sbn-label sbn-collapsible">Tutorial</span>
                                <span className="sbn-tip">Take a tour of {sectionLabel || 'this workspace'}</span>
                            </button>
                        )}

                        {navItems.map((item, i) => (
                            <NavLink
                                key={item.to || i}
                                to={item.to}
                                end={item.end}
                                className="sbn-row"
                                aria-disabled={item.locked ? 'true' : undefined}
                                onClick={e => { if (item.locked) { e.preventDefault(); setLockedLabel(item.label); } }}
                                style={item.locked ? { color: 'var(--sbn-locked-text)' } : undefined}
                            >
                                <span className="sbn-icon">{item.icon}</span>
                                <span className="sbn-label sbn-collapsible">{item.label}</span>

                                {!item.locked && !!item.badgeCount && (
                                    <span className="sbn-badge" aria-label={`${item.badgeCount} unread`}>
                                        {item.badgeCount > 99 ? '99+' : item.badgeCount}
                                    </span>
                                )}
                                {item.locked && (
                                    <span className="sbn-collapsible" style={{ display: 'flex', color: 'var(--sbn-locked-icon)' }}>
                                        <IconLock />
                                    </span>
                                )}
                                <span className="sbn-tip">
                                    {item.label}
                                    {item.locked ? ' (locked)' : ''}
                                    {!item.locked && !!item.badgeCount ? ` — ${item.badgeCount}` : ''}
                                </span>
                            </NavLink>
                        ))}
                    </nav>

                    {/* ── User block ─────────────────────────────────────────── */}
                    {user && (
                        <>
                            <hr className="sbn-rule" />
                            <div style={{ ...S.pad, paddingBottom: 4 }}>
                                <NotificationCenter
                                    user={user}
                                    variant="row"
                                    className="sbn-row"
                                    panelStyle={{ left: 'calc(100% + 8px)', right: 'auto', bottom: 0, top: 'auto' }}
                                />
                            </div>
                            <div style={{ ...S.pad, position: 'relative', paddingBottom: 12 }}>
                                <button
                                    type="button"
                                    className="sbn-row"
                                    aria-haspopup="menu"
                                    aria-expanded={menu === 'user'}
                                    onClick={(e) => { e.stopPropagation(); setMenu(m => (m === 'user' ? null : 'user')); }}
                                >
                                    <span className="sbn-avatar-ring">
                                        <span style={S.avatar} aria-hidden="true">{initials}</span>
                                    </span>
                                    <span className="sbn-label sbn-collapsible" style={{ lineHeight: 1.25 }}>
                                        <span style={{ display: 'block', fontWeight: 600, color: 'var(--sbn-text-strong)' }}>
                                            {displayName}
                                        </span>
                                        <span style={{ display: 'block', fontSize: 11, color: 'var(--sbn-text-muted)' }}>
                                            {formatRole(user.role) || user.role}
                                        </span>
                                    </span>
                                    <span className="sbn-collapsible" style={{ display: 'flex', color: 'var(--sbn-icon)' }}>
                                        <IconDots />
                                    </span>
                                    <span className="sbn-tip">{displayName} — {user.role}</span>
                                </button>

                                {menu === 'user' && (
                                    <div className="sbn-pop" role="menu" style={{ left: 12, right: 12, bottom: '100%', marginBottom: 6 }}>
                                        {/* Appearance only. Sign-out deliberately isn't here —
                                            it lives on /landing via ProfileMenu. */}
                                        <div className="sbn-caption" style={{ padding: '4px 8px 6px' }}>Appearance</div>
                                        {theme && onThemeChange ? (
                                            <div style={{ display: 'flex', gap: 4, padding: '0 4px 4px' }}>
                                                {themeOptions.map(opt => {
                                                    const on = theme === opt.key;
                                                    return (
                                                        <button
                                                            key={opt.key}
                                                            type="button"
                                                            aria-pressed={on}
                                                            onClick={() => onThemeChange(opt.key)}
                                                            className="sbn-row"
                                                            style={{
                                                                justifyContent: 'center', fontSize: 12, minHeight: 32,
                                                                background: on ? 'var(--sbn-item-active-bg)' : 'transparent',
                                                                color: on ? 'var(--sbn-item-active-text)' : 'var(--sbn-text)',
                                                                fontWeight: on ? 600 : 400,
                                                            }}
                                                        >
                                                            {opt.label}
                                                        </button>
                                                    );
                                                })}
                                            </div>
                                        ) : (
                                            <div style={{ padding: '2px 8px 8px', fontSize: 12, color: 'var(--sbn-text-muted)' }}>
                                                Themes are available in Invoicing.
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                        </>
                    )}

                    <AccessLockedModal open={!!lockedLabel} label={lockedLabel} onClose={() => setLockedLabel(null)} />
                </aside>
            </div>
        </>
    );
}

const PIN_KEY = 'sf_sidebar_pinned';

const THEME_OPTIONS = [
    { key: 'normal', label: 'Light' },
    { key: 'night',  label: 'Night' },
    { key: 'dark',   label: 'Dark' },
];

const S = {
    pad:     { padding: '8px 12px' },
    dot:     { flex: 'none', width: 8, height: 8, borderRadius: 4, marginLeft: 4, marginRight: 4 },
    currentTag: {
        flex: 'none', fontSize: 9.5, fontWeight: 800, letterSpacing: '0.06em',
        textTransform: 'uppercase', color: 'var(--sbn-text-muted)',
    },
    // NOTE: `display` is deliberately absent from both logo styles. It is owned
    // by sidebarMotion.css, which swaps monogram <-> wordmark based on whether
    // the rail is open. An inline `display` would beat the stylesheet and both
    // variants would render at once.
    logoMark: {
        flex: 'none', width: 32, height: 32, borderRadius: 8,
        background: '#FFFFFF', color: '#2C2247',
        alignItems: 'center', justifyContent: 'center',
        fontSize: 15, fontWeight: 800, letterSpacing: '-0.02em',
        border: '1px solid var(--sbn-border)', margin: '0 auto',
    },
    // Extended-state logo lockup. The wordmark is 1253x241, so at 24px tall it
    // renders ~125px wide; with 10px side padding that is 145px of the 196px
    // available beside the pin button, so it fits without crowding.
    //
    // Was 18px (~94px wide) inside a bordered chip, which read as a small boxed
    // thumbnail rather than a logo. The border is gone deliberately: the chip is
    // white, so on a light rail the outline was the only thing visible and it
    // framed the logo for no reason. On dark and night the white chip is still
    // required, because the PNG is opaque white with black lettering.
    logoChip: {
        flex: 'none', alignItems: 'center',
        background: '#FFFFFF', borderRadius: 8, padding: '5px 10px',
        fontSize: 13, fontWeight: 800, color: '#2C2247', lineHeight: 1,
    },
    avatar: {
        display: 'flex', width: 24, height: 24, borderRadius: 8,
        background: 'var(--sbn-purple-600)', color: '#fff',
        alignItems: 'center', justifyContent: 'center',
        fontSize: 10, fontWeight: 700,
    },
};

/* Inline SVG rather than an icon package — the portal has no icon dependency
   and these are the only additions. Consistent 2px stroke on a 24 box. */
const sv = { width: 16, height: 16, viewBox: '0 0 24 24', fill: 'none', stroke: 'currentColor', strokeWidth: 2, strokeLinecap: 'round', strokeLinejoin: 'round' };

// Only the "down" chevron is used — the pin button replaced the left/right
// collapse chevrons, so their path data went with them.
const IconChevron = () => <svg {...sv} aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>;
const IconMenu = () => <svg {...sv} aria-hidden="true"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>;
const IconGrid = () => <svg {...sv} aria-hidden="true"><rect x="3" y="3" width="7" height="7" rx="1.5" /><rect x="14" y="3" width="7" height="7" rx="1.5" /><rect x="3" y="14" width="7" height="7" rx="1.5" /><rect x="14" y="14" width="7" height="7" rx="1.5" /></svg>;
const IconHome = () => <svg {...sv} aria-hidden="true"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><polyline points="9 22 9 12 15 12 15 22" /></svg>;
const IconLock = () => <svg {...sv} width="13" height="13" aria-hidden="true"><rect x="3" y="11" width="18" height="11" rx="2" /><path d="M7 11V7a5 5 0 0 1 10 0v4" /></svg>;
const IconDots = () => <svg {...sv} aria-hidden="true"><circle cx="12" cy="5" r="1.6" /><circle cx="12" cy="12" r="1.6" /><circle cx="12" cy="19" r="1.6" /></svg>;
const IconPin = ({ filled }) => (
    <svg {...sv} aria-hidden="true" fill={filled ? 'currentColor' : 'none'}>
        <path d="M12 17v5" />
        <path d="M9 10.76V5a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v5.76a2 2 0 0 0 .59 1.42l1 1A1 1 0 0 1 15.88 15H8.12a1 1 0 0 1-.71-1.71l1-1A2 2 0 0 0 9 10.76Z" />
    </svg>
);
